import React from 'react'
import { TextField } from 'foundations-library/components'
import { TextFieldType } from 'foundations-library/types'
import { formatAsYouType, isValidPhoneNumber, convertNumberToE164 } from 'phone-numbers'

const formatValue = (value: string, countryCode): string => {
    let formatted = value
    if (formatted.length === 1) formatted = formatted.trim()

    if (isValidPhoneNumber(formatted) || (formatted.length > 5 && !/[a-zA-Z]/g.test(formatted))) {
        formatted = formatAsYouType(formatted, countryCode)
    }

    return formatted
}

const allowedKeys = [
    'Backspace',
    '0',
    '1',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9'
]

type PropsType = {
    onFormatChange?(v): unknown
    countryCode: string
} & Omit<TextFieldType, 'type'>

/**
 *
 */
const PhoneNumberInputField = ({
    onFormatChange,
    countryCode = 'US',
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    onSelect,
    ...props
}: PropsType): JSX.Element => {
    const moveCursorToEnd = (input: HTMLInputElement) => {
        const cursorPosition = input.value.length

        if (input.selectionStart === cursorPosition && input.selectionEnd === cursorPosition) return

        input.setSelectionRange(cursorPosition, cursorPosition)
    }

    const queueCursorToEnd = (input: HTMLInputElement) => {
        window.setTimeout(() => moveCursorToEnd(input), 0)
    }

    const handleOnChange = (newValue: string) => {
        const formattedValue = formatValue(newValue, countryCode)
        onChange?.(formattedValue)
        onFormatChange?.(convertNumberToE164(newValue))
    }

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        onFocus?.(event)
        queueCursorToEnd(event.currentTarget)
    }

    const handleOnClick = (event: React.MouseEvent<HTMLInputElement>) => {
        onClick?.(event)
        queueCursorToEnd(event.currentTarget)
    }

    const handleOnSelect = (event: React.SyntheticEvent<HTMLInputElement>) => {
        onSelect?.(event)
        queueCursorToEnd(event.currentTarget)
    }

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        onKeyDown?.(event)

        if (event.defaultPrevented) return

        if (!allowedKeys.includes(event.key)) {
            event.preventDefault()
            moveCursorToEnd(event.currentTarget)
        }
    }

    return <TextField
        type='tel'
        {...props}
        value={formatValue(props.value, countryCode)}
        onChange={handleOnChange}
        onClick={handleOnClick}
        onFocus={handleOnFocus}
        onKeyDown={handleOnKeyDown}
        onSelect={handleOnSelect}
    />
}

export default PhoneNumberInputField
